import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import { ResetIcon, UserSuccessIcon, WarningIcon } from '@sharedCR/coreDesignSystem/icons';
import { ModalSize } from '@sharedCR/coreDesignSystem/Modals';
import { uncompletedLoanErrorMessageParams } from 'app/pages/loanApplication/LoanApplication.constants';
import clsx from 'clsx';
import _ from 'lodash';
import { nanoid } from 'nanoid';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { sortArrayByDate } from 'utils/data';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import {
  openConfirmationDialogAction,
  openDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  getLoanApplicationRoleAssignmentsHistoryAction,
  showMessageThatCurrentUserCantChangeLoanAction,
} from 'store/loanApplications/actions';
import {
  selectIsCurrentUserCanChangeLoan,
  selectIsLoanInitiatedWithErrors,
} from 'store/loanApplications/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { defaultInitialEntitySideData, pagesPropTypes } from '../MainContent.constants';
import { BadgesWrapper, ContentHeadline } from '../sharedComponents';

import useLoanRoleExposures from './hooks/useLoanRoleExposures';
import LoanRole from './LoanRole';
import LoanRoleAssignmentForm, { ASSIGNMENT_FORM_TYPE } from './LoanRoleAssignmentForm';
import { requiredIndicatorStyles } from './LoanRoles.styles';
import {
  backfillAssigneeNamesFromHistory,
  checkHasMissingAssigneeNames,
  getUnfilledRequiredMessage,
  getUnfilledRequiredRoles,
} from './LoanRoles.utils';
import LoanRolesHistory from './LoanRolesHistory';

const LoanRoles = ({
  isLoading,
  isSaving,
  className,
  applicationData,
  initialEntitySideData = defaultInitialEntitySideData,
  entitySideData,
  setInitialEntitySideData,
  setEntitySideData,
  pendingChanges,
}) => {
  const dispatch = useDispatch();

  const isLoanInitiatedWithErrors = useSelector(selectIsLoanInitiatedWithErrors);
  const isCurrentUserCanChangeLoan = useSelector(selectIsCurrentUserCanChangeLoan);
  const { isEditAllowed, isManageAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_EDITOR_LOAN_ROLE_ASSIGNEMENT),
  );

  const { isBroker, actionableRoleIds, requiredExposures } = useLoanRoleExposures();

  const loanRolesPendingChangesRef = useRef({});

  const { loanRoles: savedLoanRoles, initiallyNewAssignedLoanRoles } = applicationData;
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  const { loanRoles = [] } = entitySideData;
  const isDataLoading = isLoading && !isSaving;

  const unfilledRequiredMessage = useMemo(
    () => getUnfilledRequiredMessage(getUnfilledRequiredRoles(requiredExposures, loanRoles)),
    [requiredExposures, loanRoles],
  );

  const hasMissingAssigneeNames = useMemo(
    () => checkHasMissingAssigneeNames(loanRoles),
    [loanRoles],
  );

  useEffect(() => {
    if (initialEntitySideData.loanRoles || !savedLoanRoles) {
      return;
    }

    const initialLoanRolesData = {
      loanRoles: savedLoanRoles,
      newAssignedLoanRoles: [],
      unassignedLoanRoles: [],
      annihilatedLoanRoles: [],
      reassignedLoanRoles: [],
    };

    setEntitySideData(prev => ({
      ...prev,
      ...initialLoanRolesData,
    }));
    setInitialEntitySideData(prev => ({
      ...prev,
      ...initialLoanRolesData,
    }));
  }, [
    initialEntitySideData,
    applicationData,
    savedLoanRoles,
    setEntitySideData,
    setInitialEntitySideData,
  ]);

  useEffect(() => {
    loanRolesPendingChangesRef.current = pendingChanges.loanRoles ?? {};
  }, [pendingChanges]);

  useEffectSafeStrictMode(() => {
    if (!hasMissingAssigneeNames) {
      return undefined;
    }

    const subscription = { isActive: true };

    dispatch(getLoanApplicationRoleAssignmentsHistoryAction({ silent: true }))
      .then(history => {
        if (!subscription.isActive) {
          return;
        }

        const withBackfilledNames = prev => ({
          ...prev,
          loanRoles: backfillAssigneeNamesFromHistory(prev.loanRoles ?? [], history),
        });

        setEntitySideData(withBackfilledNames);
        setInitialEntitySideData(withBackfilledNames);
      })
      .catch(_.noop);

    return () => {
      subscription.isActive = false;
    };
  }, [hasMissingAssigneeNames, dispatch, setEntitySideData, setInitialEntitySideData]);

  const onAssignLoanRole = useCallback(
    assignmentData => {
      const {
        loanRoleId: loanRoleIdToAssign,
        loanRoleName: loanRoleNameToAssign,
        userId: userIdToAssign,
        userName: userNameToAssign,
      } = assignmentData;

      setEntitySideData(prev => {
        const { newAssignedLoanRoles, loanRoles, annihilatedLoanRoles, unassignedLoanRoles } = prev;

        const isCurrentlyAnnihilated = annihilatedLoanRoles.includes(loanRoleIdToAssign);
        const willAnnihilatedAfterUpdate =
          loanRolesPendingChangesRef.current[loanRoleIdToAssign]?.assignee?.initialValue ===
          userNameToAssign;

        const restoredLoanRole = isCurrentlyAnnihilated
          ? savedLoanRoles.find(({ loanRoleId }) => loanRoleId === loanRoleIdToAssign)
          : null;

        const isRestoredLoanRoleHasSameUser = restoredLoanRole?.userName === userNameToAssign;

        const loanRoleToAssign = isCurrentlyAnnihilated
          ? {
              ...restoredLoanRole,
              userName: userNameToAssign,
              assignedDate: isRestoredLoanRoleHasSameUser
                ? restoredLoanRole.assignedDate
                : new Date(),
            }
          : {
              loanRoleId: loanRoleIdToAssign,
              userName: userNameToAssign,
              roleName: loanRoleNameToAssign,
              assignedDate: new Date(),
              isNewAssigned: true,
              userId: userIdToAssign,
            };

        const updatedLoanRoles = [loanRoleToAssign, ...loanRoles];

        const sortedUpdatedLoanRoles =
          isCurrentlyAnnihilated && isRestoredLoanRoleHasSameUser
            ? sortArrayByDate(updatedLoanRoles, 'assignedDate', 'desc')
            : updatedLoanRoles;

        const updatedNewAssignedLoanRoles = isCurrentlyAnnihilated
          ? newAssignedLoanRoles
          : [...newAssignedLoanRoles, loanRoleIdToAssign];

        const updatedUnassignedLoanRoles = isCurrentlyAnnihilated
          ? _.without(unassignedLoanRoles, loanRoleIdToAssign)
          : unassignedLoanRoles;

        const updatedAnnihilatedLoanRoles =
          isCurrentlyAnnihilated && !willAnnihilatedAfterUpdate
            ? _.without(annihilatedLoanRoles, loanRoleIdToAssign)
            : annihilatedLoanRoles;

        return {
          ...prev,
          loanRoles: sortedUpdatedLoanRoles,
          newAssignedLoanRoles: updatedNewAssignedLoanRoles,
          unassignedLoanRoles: updatedUnassignedLoanRoles,
          annihilatedLoanRoles: updatedAnnihilatedLoanRoles,
        };
      });
    },
    [setEntitySideData, savedLoanRoles],
  );

  // DEV-4826: a broker assignment persists immediately via the dedicated comp-free endpoint, so the
  // display update is optimistic-only — it must NOT feed the pending-changes tracking arrays
  // (newAssignedLoanRoles/reassignedLoanRoles), which would re-commit it through the comp-validated
  // loan-info save and 409 on an unpriced loan.
  const onBrokerAssignSaved = useCallback(
    ({ loanRoleId: loanRoleIdToAssign, loanRoleName, userId, userName }) => {
      setEntitySideData(prev => ({
        ...prev,
        loanRoles: [
          {
            loanRoleId: loanRoleIdToAssign,
            roleName: loanRoleName,
            userName,
            userId,
            assignedDate: new Date(),
          },
          ...(prev.loanRoles ?? []).filter(({ loanRoleId }) => loanRoleId !== loanRoleIdToAssign),
        ],
      }));
    },
    [setEntitySideData],
  );

  const onBrokerReassignSaved = useCallback(
    ({ loanRoleId: loanRoleIdToReassign, userId, userName }) => {
      setEntitySideData(prev => ({
        ...prev,
        loanRoles: (prev.loanRoles ?? []).map(loanRole =>
          loanRole.loanRoleId === loanRoleIdToReassign
            ? { ...loanRole, userId, userName }
            : loanRole,
        ),
      }));
    },
    [setEntitySideData],
  );

  const onReassignLoanRole = useCallback(
    assignmentData => {
      const {
        loanRoleId: loanRoleIdToReassign,
        userId: userIdToAssign,
        userName: userNameToAssign,
      } = assignmentData;

      setEntitySideData(prev => {
        const { newAssignedLoanRoles, loanRoles, reassignedLoanRoles, annihilatedLoanRoles } = prev;

        const loanRoleIndexToReassign = loanRoles.findIndex(
          ({ loanRoleId }) => loanRoleIdToReassign === loanRoleId,
        );

        const savedLoanRole = savedLoanRoles.find(
          ({ loanRoleId }) => loanRoleIdToReassign === loanRoleId,
        );

        const existsReassignment =
          savedLoanRole?.userName === userNameToAssign ? savedLoanRole : null;

        const isNewAssigned = newAssignedLoanRoles.includes(loanRoleIdToReassign);
        const isInitiallyNewAssigned = initiallyNewAssignedLoanRoles.includes(loanRoleIdToReassign);
        const willAnnihilatedAfterUpdate =
          savedLoanRole?.userName !== userNameToAssign &&
          loanRolesPendingChangesRef.current[loanRoleIdToReassign]?.assignee?.initialValue ===
            userNameToAssign;

        const loanRoleToReassign = existsReassignment ?? {
          ...loanRoles[loanRoleIndexToReassign],
          userName: userNameToAssign,
          userId: userIdToAssign,
          assignedDate:
            isNewAssigned || isInitiallyNewAssigned ? new Date() : savedLoanRole?.assignedDate,
          isReassigned:
            isInitiallyNewAssigned || isNewAssigned ? false : !willAnnihilatedAfterUpdate,
          isAnnihilated: willAnnihilatedAfterUpdate,
        };

        const updatedAnnihilatedLoanRoles = willAnnihilatedAfterUpdate
          ? [...annihilatedLoanRoles, loanRoleIdToReassign]
          : _.without(annihilatedLoanRoles, loanRoleIdToReassign);

        const updatedLoanRoles = sortArrayByDate(
          [
            ...loanRoles.filter(({ loanRoleId }) => loanRoleId !== loanRoleIdToReassign),
            loanRoleToReassign,
          ],
          'assignedDate',
          'desc',
        );

        const updatedReassignedLoanRoles =
          (isNewAssigned && reassignedLoanRoles) || existsReassignment
            ? _.without(reassignedLoanRoles, loanRoleIdToReassign)
            : _.uniq([...reassignedLoanRoles, loanRoleIdToReassign]);

        return {
          ...prev,
          loanRoles: updatedLoanRoles,
          reassignedLoanRoles: updatedReassignedLoanRoles,
          annihilatedLoanRoles: updatedAnnihilatedLoanRoles,
        };
      });
    },
    [setEntitySideData, savedLoanRoles, initiallyNewAssignedLoanRoles],
  );

  const onUnassignLoanRole = useCallback(
    loanRoleIdToUnassign => {
      const onAccept = () => {
        setEntitySideData(prev => {
          const {
            newAssignedLoanRoles,
            loanRoles,
            annihilatedLoanRoles,
            unassignedLoanRoles,
            reassignedLoanRoles,
          } = prev;

          const isNewAssigned = newAssignedLoanRoles.includes(loanRoleIdToUnassign);
          const isInitiallyNewAssigned =
            initiallyNewAssignedLoanRoles.includes(loanRoleIdToUnassign);
          const isReassigned = reassignedLoanRoles.includes(loanRoleIdToUnassign);

          const updatedNewAssignedLoanRoles = isNewAssigned
            ? _.without(newAssignedLoanRoles, loanRoleIdToUnassign)
            : newAssignedLoanRoles;

          const updatedUnassignedLoanRoles = isNewAssigned
            ? unassignedLoanRoles
            : [...unassignedLoanRoles, loanRoleIdToUnassign];

          const updatedAnnihilatedLoanRoles = isInitiallyNewAssigned
            ? [...annihilatedLoanRoles, loanRoleIdToUnassign]
            : annihilatedLoanRoles;

          const updatedReassignedLoanRoles = isReassigned
            ? _.without(reassignedLoanRoles, loanRoleIdToUnassign)
            : reassignedLoanRoles;

          const updatedLoanRoles = loanRoles.reduce((acc, loanRole) => {
            const { isNewAssigned, isInitiallyNewAssigned, loanRoleId } = loanRole;

            if (loanRoleIdToUnassign !== loanRoleId) {
              acc.push(loanRole);
            }

            if (loanRoleIdToUnassign === loanRoleId && !isNewAssigned && !isInitiallyNewAssigned) {
              acc.push({
                ...savedLoanRoles.find(({ loanRoleId }) => loanRoleId === loanRoleIdToUnassign),
                isUnassigned: true,
              });
            }

            return acc;
          }, []);

          return {
            ...prev,
            newAssignedLoanRoles: updatedNewAssignedLoanRoles,
            unassignedLoanRoles: updatedUnassignedLoanRoles,
            annihilatedLoanRoles: updatedAnnihilatedLoanRoles,
            loanRoles: updatedLoanRoles,
            reassignedLoanRoles: updatedReassignedLoanRoles,
          };
        });
      };

      dispatch(
        openConfirmationDialogAction({
          message: 'Do you want to un-assign this loan role?',
          type: ConfirmationModalType.WARNING,
          onAcceptButtonName: 'Yes',
          onCancelButtonName: 'No',
          onAccept,
        }),
      );
    },
    [setEntitySideData, initiallyNewAssignedLoanRoles, savedLoanRoles, dispatch],
  );

  const onRestoreLoanRole = useCallback(
    e => {
      const { loanRoleId: loanRoleIdToRestore } = e.currentTarget.dataset;

      setEntitySideData(prev => {
        const { unassignedLoanRoles, loanRoles } = prev;

        const updatedUnassignedLoanRoles = _.without(unassignedLoanRoles, loanRoleIdToRestore);

        const updatedLoanRoles = sortArrayByDate(
          loanRoles.map(loanRole =>
            loanRole.loanRoleId === loanRoleIdToRestore
              ? { ...loanRole, isUnassigned: false }
              : loanRole,
          ),
          'assignedDate',
          'desc',
        );

        return {
          ...prev,
          unassignedLoanRoles: updatedUnassignedLoanRoles,
          loanRoles: updatedLoanRoles,
        };
      });
    },
    [setEntitySideData],
  );

  const onOpenLoanRolesHistory = useCallback(() => {
    const dialogId = nanoid();

    dispatch(
      openDialogAction({
        id: dialogId,
        isRestyled: true,
        header: 'Loan Roles History',
        size: ModalSize.LARGE,
        hasScrollY: false,
        body: () => <LoanRolesHistory />,
      }),
    );
  }, [dispatch]);

  const openAssignmentRoleForm = useCallback(
    ({ type, ...restProps }) => {
      const dialogId = nanoid();

      if (isLoanInitiatedWithErrors) {
        return dispatch(showMessageAction(uncompletedLoanErrorMessageParams));
      }

      if (!isCurrentUserCanChangeLoan) {
        return dispatch(showMessageThatCurrentUserCantChangeLoanAction());
      }

      const title = `Loan Role ${
        type === ASSIGNMENT_FORM_TYPE.ASSIGN ? 'Assignment' : 'Reassignment'
      }`;

      dispatch(
        openDialogAction({
          id: dialogId,
          isRestyled: true,
          header: title,
          hasScrollY: false,
          body: dialogProps => (
            <LoanRoleAssignmentForm
              {...dialogProps}
              dialogId={dialogId}
              type={type}
              {...restProps}
            />
          ),
        }),
      );
    },
    [dispatch, isLoanInitiatedWithErrors, isCurrentUserCanChangeLoan],
  );

  const onOpenAssignLoanRoleForm = useCallback(() => {
    const loanRolesToExclude = loanRoles.map(({ loanRoleId }) => loanRoleId);

    openAssignmentRoleForm({
      type: ASSIGNMENT_FORM_TYPE.ASSIGN,
      loanRolesToExclude,
      onAssignLoanRole: isBroker ? onBrokerAssignSaved : onAssignLoanRole,
      isBroker,
      allowedLoanRoleIds: isBroker ? actionableRoleIds : null,
    });
  }, [
    loanRoles,
    onAssignLoanRole,
    onBrokerAssignSaved,
    openAssignmentRoleForm,
    isBroker,
    actionableRoleIds,
  ]);

  const onOpenReassignLoanRoleForm = useCallback(
    (loanRoleId, userName) => {
      const currentUserId =
        loanRoles.find(loanRole => loanRole.loanRoleId === loanRoleId)?.userId ?? null;

      openAssignmentRoleForm({
        type: ASSIGNMENT_FORM_TYPE.REASSIGN,
        onReassignLoanRole: isBroker ? onBrokerReassignSaved : onReassignLoanRole,
        loanRoleId,
        userName,
        currentUserId,
        isBroker,
        allowedLoanRoleIds: isBroker ? actionableRoleIds : null,
      });
    },
    [
      loanRoles,
      onReassignLoanRole,
      onBrokerReassignSaved,
      openAssignmentRoleForm,
      isBroker,
      actionableRoleIds,
    ],
  );

  return (
    <Box className={clsx('overflow-x-hidden', className)}>
      <ContentHeadline
        controls={
          <ButtonGroupWrapper className="items-center">
            <Button
              data-testid={PropertyAndLoanTestIds.LOAN_ROLES_HISTORY_BUTTON}
              disabled={isLoading}
              size="small"
              startIcon={<ResetIcon size={IconSize[16]} />}
              variant="outlined"
              onClick={onOpenLoanRolesHistory}
            >
              History
            </Button>
            {isManageAllowed && (
              <Button
                data-testid={PropertyAndLoanTestIds.LOAN_ROLES_ASSIGN_BUTTON}
                disabled={isLoading}
                size="small"
                startIcon={<UserSuccessIcon size={IconSize[16]} />}
                variant="outlined"
                onClick={onOpenAssignLoanRoleForm}
              >
                Assign
              </Button>
            )}
          </ButtonGroupWrapper>
        }
        title="Loan Roles"
      />

      {!!unfilledRequiredMessage && (
        <Box
          data-testid={PropertyAndLoanTestIds.LOAN_ROLES_REQUIRED_INDICATOR}
          sx={requiredIndicatorStyles}
        >
          <WarningIcon size={IconSize[16]} />
          <Typography color="text.dark" component="p" variant="regular12">
            {unfilledRequiredMessage}
          </Typography>
        </Box>
      )}

      <BadgesWrapper data={loanRoles} isDataLoading={isDataLoading} skeletonHeight={102}>
        {loanRoles.map(loanRole => (
          <LoanRole
            isActionableForCurrentSubCompany={
              !isBroker || actionableRoleIds.includes(loanRole.loanRoleId)
            }
            isEditAllowed={isEditAllowed}
            isLoading={isLoading}
            isManageAllowed={isManageAllowed}
            key={loanRole.loanRoleId}
            loanRole={loanRole}
            onOpenReassignLoanRoleForm={onOpenReassignLoanRoleForm}
            onRestoreLoanRole={onRestoreLoanRole}
            onUnassignLoanRole={onUnassignLoanRole}
          />
        ))}
      </BadgesWrapper>
    </Box>
  );
};

LoanRoles.propTypes = pagesPropTypes;

export default memo(LoanRoles);
